import type { Router } from "vue-router";
import { setRouteEmitter } from "@/utils/route-listener";
// import setupPermissionGuard from './permission';
import { useAppStore, useTabBarStore, useUserStore } from "@/store";
//import appRoutes from "@/router/routes";
//import adminRoutes from "@/router/admin-routers";
import DefaultLayout from "@/layout/default-layout.vue";
import Nofound from "@/views/not-found/index.vue";
import { isLogin } from "@/utils/auth";
import usePermission from "@/hooks/permission";

// 添加chunk加载失败的错误处理
const handleChunkError = () => {
  // 监听chunk加载失败事件
  window.addEventListener('unhandledrejection', event => {
    if (event.reason && event.reason.message && event.reason.message.includes('Loading chunk')) {
      console.warn('Chunk加载失败，尝试重新加载页面:', event.reason.message);
      // 延迟重试，避免无限循环
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  });

  // 监听全局错误
  window.addEventListener('error', event => {
    if (event.message && event.message.includes('Loading chunk')) {
      console.warn('Chunk加载错误，尝试重新加载页面:', event.message);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  });
};

const notFound: any = {
  path: "/:pathMatch(.*)*",
  name: "notFound",
  component: DefaultLayout,
  redirect: "/permission",
  children: [
    {
      name: "permission",
      path: "/permission",
      component: Nofound,
    },
  ],
};
const dashboard: any = {
  path: "/h5",
  name: "h5",
  meta: {
    locale: "h5",
    requiresAuth: false,
    hideInMenu: true,
  },
  children: [
    {
      path: "dashboard-train",
      name: "dashboardTrain",
      component: () => import("@/views/h5/train.vue"),
      meta: {
        locale: "训练数据大屏",
        requiresAuth: false,
        roles: ["*"],
        hideInMenu: true,
      },
    },
  ],
}

// import { resetRouter } from "../reset";

function setupPageGuard(router: Router) {
  const addRoutes = (routes: any[], parentName: string = ""): void => {
    const tabBarStore = useTabBarStore();
    routes.forEach((item) => {
      if (item.path && item.component) {
        const componentPath = item.component
          .toString()
          .substring(2)
          .replace(/\.\w+$/, "");
        
        // 优化动态导入，添加错误处理
        const route = {
          path: item.path,
          redirect: item.redirect == "noRedirect" ? undefined : item.redirect,
          name: item.name,
          component: () => {
            return import(/* webpackChunkName: "[request]" */ "@/" + componentPath + ".vue")
              .catch(error => {
                console.error(`组件加载失败: ${componentPath}`, error);
                // 如果组件加载失败，返回一个错误页面组件
                return import("@/views/not-found/index.vue");
              });
          },
          meta: item.meta,
        };
        if (route.path.includes("trainView")) {
          route.redirect = "/h5/dashboard-train";
        }
        if (route.path.includes("examView")) {
          route.redirect = "/h5/dashboard-exam";
        }
        if (route.path.includes("leaderView")) {
          route.redirect = "/app/leader";
        }
        if (route.path.includes("personAnalysis")) {
          route.redirect = "/app/person";
        }
        if (route.path.includes("holoView")) {
          route.redirect = "/h5/dashboard-holo";
        }
        
        try {
          parentName ? router.addRoute(parentName, route) : router.addRoute(route);
        } catch (error) {
          console.error(`路由添加失败: ${item.path}`, error);
        }
        
        if (item.children && item.children.length) {
          addRoutes(item.children, item.name);
        }
      }
      if (!item.meta.noCache) {
        tabBarStore.updateTabList(item);
      }
    });
  };
  router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore();
    const appStore = useAppStore();
    const Permission = usePermission();
    window.document.title = "军人体能训练管理考核系统"; //设置固定标题
    if (Permission.accessRouter(to)) {
      next();
    } else {
      // 解决菜单（难易度配置）跳转其他 菜单失败问题
      // if (from.fullPath === "/app/rank/config") {
      //   setTimeout(() => {
      //     // next()
      //     location.reload();
      //   }, 50);
      // }
      if (isLogin()) {
        if (to.path == "/login" || to.path == "/auth") {
          next("/home");
        } else {
          await userStore.checkUserInfo();
          if (appStore.hasRoutes) {
            next();
          } else {
            let barrackId = userStore.loginBarrackId;
            try {
              const permissionRoutes: any[] = await userStore.getUserRouters();
              if (!barrackId) {
                const admin: any = {
                  name: "admin",
                  path: "/admin",
                  component: DefaultLayout,
                  redirect: "/admin/home",
                  meta: {
                    locale: "管理平台",
                    requiresAuth: true,
                    roles: ["*"],
                  },
                };
                router.addRoute(admin);
                addRoutes(permissionRoutes, "admin");
                router.addRoute(notFound);
                appStore.setHasRoutes(true);
                next({ ...to });
              } else {
                const user: any = {
                  name: "app",
                  path: "/app",
                  redirect: "/app/index",
                  meta: { locale: "平台" },
                  component: DefaultLayout,
                };
                router.addRoute(user);
                addRoutes(permissionRoutes, "app");
                router.addRoute(notFound);
                appStore.setHasRoutes(true);
                next({ ...to, replace: true });
              }
            } catch (error) {
              userStore.logout().then(() => {
                appStore.setHasRoutes(false);
                window.location.reload();
              });
            }
          }
        }
      } else {
        if (to.path == "/auth") {
          next();
        } else if (to.path !== "/login") {
          next("/login");
        } else {
          next();
        }
      }
      // emit route change
      setRouteEmitter(to);
    }
  });
}

export default function createRouteGuard(router: Router) {
  setupPageGuard(router);
  // setupPermissionGuard(router);
  handleChunkError(); // 调用错误处理函数
}
